import { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';

import { TshirtPanelCustomization } from './components/my-team/TshirtPanelCustomization';
import { parseParamsTshirtAndShield, tshirtKeys } from './functions/utils';

import { Button, Loader, Tabs } from '@/components';
import { TshirtTeam } from '@/components/TshirtTeam';
import { TabType } from '@/components/Types';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import useToast from '@/hooks/useToast';
import useTshirtAndShield from '@/hooks/useTshirtAndShield';
import ROUTES from '@/router/routes';
import { saveTshirt } from '@/services/editAccount';
import {
  defaultTshirtTeam,
  initialTshitAndShield,
} from '@/store/tshirtAndShield';
import { Svg, SvgCamiseta } from '@/types/editProfile';

export const TshirtPanel = () => {
  const toast = useToast();
  const queryClient = useQueryClient();

  const { updateTshirtAndShield, data: dataShieldTshirt } =
    useTshirtAndShield();

  const [customizationValues, setCustomizationValues] = useState<
    Svg | undefined
  >({ camiseta: dataShieldTshirt?.tshirt });

  const { isDesktop, isMobile } = useIsDesktop();

  const navigate = useNavigate();

  const handleCustomizationValuesChange = (customizationValues: Svg) => {
    setCustomizationValues(customizationValues);
  };

  const normalize = (v: string | null | undefined) => {
    // convierte null/undefined a '', elimina espacios y trata los strings "null"/"undefined" como vacíos
    const raw = v ?? '';
    if (raw === 'null' || raw === 'undefined') return '';
    return String(raw).trim();
  };

  const disabledSubmit = useMemo(() => {
    const tshirt = dataShieldTshirt?.tshirt ?? {};
    const cam = customizationValues?.camiseta ?? {};
    return (
      normalize(tshirt.param1) === normalize(cam.param1) &&
      normalize(tshirt.param2) === normalize(cam.param2) &&
      normalize(tshirt.param3) === normalize(cam.param3) &&
      normalize(tshirt.param4) === normalize(cam.param4) &&
      normalize(tshirt.param5) === normalize(cam.param5)
    );
  }, [customizationValues?.camiseta, dataShieldTshirt?.tshirt]);

  const handleSaveChanges = async () => {
    try {
      const response = await saveTshirt({
        param1:
          customizationValues?.camiseta?.param1 ?? defaultTshirtTeam.param1,
        param2:
          customizationValues?.camiseta?.param2 ?? defaultTshirtTeam.param2,
        param3:
          customizationValues?.camiseta?.param3 ?? defaultTshirtTeam.param3,
        param4:
          customizationValues?.camiseta?.param4 ?? defaultTshirtTeam.param4,
        param5:
          customizationValues?.camiseta?.param5 ?? defaultTshirtTeam.param5,
      });

      if (response) {
        if (response.estado) {
          toast.show('Se grabaron los cambios de tu camiseta');
          queryClient.invalidateQueries('initialDataTshitAndShield');
          // Generamos los datos dinámicamente para actualizar el estado global
          const tshirtData = parseParamsTshirtAndShield<SvgCamiseta>(
            response.camiseta_svg,
            tshirtKeys,
            initialTshitAndShield.tshirt
          );

          updateTshirtAndShield({
            tshirt: tshirtData,
          });
        }
        toast.show(response.mensaje);
      }
    } catch (error) {
      toast.show('Ocurrió un error al guardar los cambios');
    }
  };

  const handleGoBack = () => {
    navigate(ROUTES.editAccount.myTeam);
    queryClient.invalidateQueries('initialDataTshitAndShield');
  };

  const ActionButtonSaveTshirt = () => {
    return (
      <Button
        className="lg:mt-20 lg:w-60 w-full"
        disabled={disabledSubmit}
        type="button"
        variant={'primary'}
        onClick={handleSaveChanges}
      >
        Guardar cambios
      </Button>
    );
  };

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (loading) setLoading(() => false);
  }, [loading]);

  return (
    <>
      <Loader isVisible={loading} />
      <div className="lg:mt-7 flex flex-wrap pb-28 lg:pb-0">
        {isMobile && (
          <Tabs
            activeTab={TabType.FirstTab}
            className="w-full px-4 mt-4"
            tabs={[{ name: 'Mi camiseta' }, { name: 'Escudo' }]}
            onClick={(tab) =>
              tab === TabType.SecondTab &&
              navigate(ROUTES.editAccount.shieldPanel)
            }
          />
        )}
        <div className="lg:pl-[50px] text-gdt-blue text-lg font-semibold cursor-pointer hidden lg:flex gap-2">
          <p>{'<'}</p>
          <Button
            className="text-[16px] font-bold inline-block no-underline"
            type="button"
            variant="text"
            onClick={handleGoBack}
          >
            Volver
          </Button>
        </div>

        <div className="flex justify-center flex-col lg:flex-row">
          <div className="w-screen lg:max-w-[460px] text-center">
            <figure className="lg:max-w-56 max-w-52 mt-4 lg:mt-10 mx-auto">
              <TshirtTeam
                withShield
                shield={dataShieldTshirt?.shield}
                tshirt={{
                  ...dataShieldTshirt?.tshirt,
                  ...customizationValues?.camiseta,
                }}
              />
            </figure>
            {isDesktop && <ActionButtonSaveTshirt />}
          </div>
          <TshirtPanelCustomization
            customizationValues={customizationValues}
            handleCustomizationValuesChange={handleCustomizationValuesChange}
          />
        </div>
        {isMobile && (
          <div className="fixed bottom-0 left-0 z-10 w-full bg-white flex justify-center border-t shadow-sm p-4">
            <ActionButtonSaveTshirt />
          </div>
        )}
      </div>
    </>
  );
};
